// src/components/FloatingCompanyBrand.jsx
import { Box, Typography } from '@mui/material';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import { useContext, useLayoutEffect, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import Logo from "../../assets/images/plany365.ico";
import { UserContext } from '../auth/auth-context';
import { GET_BILLING_SUBSCRIPTION, GET_BUSINESS_BY_URL } from '../../graphql/queries';
import { useSelectedBusiness } from '../../redux/store';
import {
  getStoredFloatingBrandPreviousPath,
  resolveFloatingBrandNavigation,
  shouldShowPublicBusinessBrand,
} from "./floatingCompanyBrand.helpers";

const PLANY365_HOME_URL = "/";

export default function FloatingCompanyBrand() {
  const location = useLocation();
  const navigate = useNavigate();
  const [subscription, setSubscription] = useState(null);
  const { providerUser } = useContext(UserContext);
  const selectedBusiness = useSelectedBusiness();

  const firstSeg = useMemo(
    () => location.pathname.split('/').filter(Boolean)[0] || '',
    [location.pathname]
  );
  const publicBusinessHomeHref = useMemo(() => {
    const segments = location.pathname.split("/").filter(Boolean);
    if (segments[0] !== "b" || !segments[1]) return null;
    return `/b/${segments[1]}`;
  }, [location.pathname]);
  const publicBusinessUrl = useMemo(() => {
    const segments = location.pathname.split("/").filter(Boolean);
    if (segments[0] !== "b" || !segments[1]) return "";
    return segments[1];
  }, [location.pathname]);
  const isAuthPage =
    firstSeg === 'login' || firstSeg === 'signup' || firstSeg === 'forgotpassword';
  const isCaptureInvitePage = firstSeg === "i";
  const authBackHref = PLANY365_HOME_URL;
  const currentPath = `${location.pathname}${location.search}${location.hash}`;
  const storedPreviousPath = useMemo(
    () => getStoredFloatingBrandPreviousPath(window.sessionStorage),
    [currentPath]
  );

  const { loading, data } = useQuery(GET_BILLING_SUBSCRIPTION, {
    variables: { businessId: providerUser?.defaultBusinessId },
    skip: isAuthPage || !providerUser?.defaultBusinessId,
  });
  const { loading: publicBusinessLoading, data: publicBusinessData } = useQuery(GET_BUSINESS_BY_URL, {
    variables: { businessUrl: publicBusinessUrl },
    skip: isAuthPage || !publicBusinessUrl,
    fetchPolicy: "cache-first",
  });

  useLayoutEffect(() => {
    if (data?.getBillingSubscription) {
      setSubscription({
        ...data.getBillingSubscription,
        businessBasic: data.getBillingSubscription,
      });
    }
  }, [data]);

  const viewedPublicBusiness = publicBusinessData?.businessByUrl || null;
  const showPublicBusinessBrand =
    Boolean(publicBusinessHomeHref) &&
    !isAuthPage &&
    viewedPublicBusiness != null &&
    shouldShowPublicBusinessBrand(viewedPublicBusiness);
  const backOrHomeNavigation = useMemo(
    () =>
      resolveFloatingBrandNavigation({
        currentPath,
        fromState: location.state?.from,
        storedPreviousPath,
        referrer: document.referrer,
        origin: window.location.origin,
      }),
    [currentPath, location.state, storedPreviousPath]
  );

  if (loading || (publicBusinessUrl && publicBusinessLoading)) return null;

  const planId =
    (subscription?.planId && Number(subscription?.planId)) ||
    (providerUser?.planId && Number(providerUser?.planId)) ||
    1;
  const businessName = selectedBusiness?.businessBasic?.name || "";
  const navigateBackOrHome = () => {
    navigate(backOrHomeNavigation.to, { replace: true });
  };

  const handleClick = () => {
    if (isAuthPage) {
      // ✅ Reset last vertical and force chooser
      navigate(authBackHref, { replace: true });
      return;
    }

    if (isCaptureInvitePage && providerUser) {
      navigate("/biz/capture", { replace: true });
      return;
    }

    if (publicBusinessHomeHref || planId === 1) {
      navigateBackOrHome();
      return;
    }

    if (planId > 1 && firstSeg) {
      navigate(`/${firstSeg}`);
      return;
    }

    navigateBackOrHome();
  };

  if (showPublicBusinessBrand) {
    return (
      <Box
        component="button"
        type="button"
        onClick={navigateBackOrHome}
        sx={{
          position: "fixed",
          top: '0.1px',
          left: '50%',
          transform: 'translateX(-50%)',
          borderRadius: "8px",
          cursor: "pointer",
          background: "transparent",
          border: 0,
          padding: 0,
          pointerEvents: "auto",
          zIndex: 1400,
          display: "inline-flex",
          "&:hover": { backgroundColor: "#f5f5f5" },
        }}
      >
        <img src={Logo} alt="Company Logo" style={{ width: 50, height: 50 }} />
      </Box>
    );
  }

  if (publicBusinessHomeHref && !isAuthPage) {
    return null;
  }

  if (planId > 1 && !isAuthPage && firstSeg) {
    return (
      <Box
        component={RouterLink}
        to={`/${firstSeg}`}
        sx={{
          position: "fixed",
          top: "0.1px",
          left: "50%",
          transform: "translateX(-50%)",
          borderRadius: "8px",
          cursor: "pointer",
          textDecoration: "none",
          "&:hover": { backgroundColor: "#f5f5f5" },
        }}
      >
        <Typography variant="h6" component="span">
          {businessName}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      component="button"
      type="button"
      sx={{
        position: "fixed",
        top: '0.1px',
        left: '50%',
        transform: 'translateX(-50%)',
        borderRadius: "8px",
        cursor: "pointer",
        background: "transparent",
        border: 0,
        padding: 0,
        pointerEvents: "auto",
        zIndex: 1400,
        "&:hover": { backgroundColor: "#f5f5f5" },
      }}
      onClick={handleClick}
    >
      <img src={Logo} alt="Company Logo" style={{ width: 50, height: 50 }} />
    </Box>
  );
}
